import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search, Package, Clock, ChevronLeft, ChevronRight, AlertCircle, ImageIcon
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

interface Service {
  id: string;
  name: string;
  description?: string;
  duration_minutes?: number;
  price_from?: number;
  currency?: string;
  status: string;
  language?: string;
  image_url?: string;
}

interface ServiceStats {
  total: number;
  active: number;
  draft: number;
  archived: number;
  languages: string[];
}

function formatDuration(minutes?: number): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active': return 'default';
    case 'draft': return 'secondary';
    case 'archived': return 'outline';
    default: return 'secondary';
  }
}

export default function ServiceCatalog() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();

  const [services, setServices] = useState<Service[]>([]);
  const [stats, setStats] = useState<ServiceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 24;

  const siteId = selectedSite?.id;

  // Fetch stats
  useEffect(() => {
    if (!siteId) return;
    setStatsLoading(true);
    authFetch(`/api/services/stats/${siteId}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Stats API returned ${res.status}`);
        const data = await res.json();
        setStats(data);
      })
      .catch((err) => {
        console.error('Failed to fetch service stats:', err);
      })
      .finally(() => setStatsLoading(false));
  }, [siteId]);

  // Fetch services
  const fetchServices = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
      });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (languageFilter !== 'all') params.set('language', languageFilter);

      const res = await authFetch(`/api/services/${siteId}?${params.toString()}`);
      if (!res.ok) throw new Error(`Services API returned ${res.status}`);
      const data = await res.json();

      setServices(data.services || data.data || []);
      setTotalCount(data.total ?? data.count ?? 0);
      setTotalPages(data.totalPages ?? (Math.ceil((data.total ?? 0) / LIMIT) || 1));
    } catch (err: any) {
      setError(err.message || 'Failed to load services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, page, search, statusFilter, languageFilter]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, languageFilter, siteId]);

  // No site selected
  if (!siteId) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-4">Service Catalog</h1>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No site selected</h3>
            <p className="text-muted-foreground">Select a site from the header to view its service catalog.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Service Catalog</h1>
        <p className="text-muted-foreground">
          Browse and manage services offered by your business.
        </p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))
        ) : stats ? (
          <>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Services</p>
                <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.draft.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Archived</p>
                <p className="text-2xl font-bold text-gray-500">{stats.archived.toLocaleString()}</p>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search services by name or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {stats?.languages && stats.languages.length > 1 && (
          <Select value={languageFilter} onValueChange={setLanguageFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {stats.languages.map((lang) => (
                <SelectItem key={lang} value={lang}>{lang.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Error */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchServices} className="ml-auto">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading Skeleton */}
      {loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: LIMIT > 12 ? 12 : LIMIT }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6 space-y-3">
                <Skeleton className="h-32 w-full rounded" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && services.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No services found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter !== 'all' || languageFilter !== 'all'
                ? 'Try adjusting your filters or search query.'
                : 'Services will appear here once they are imported or created. Add services through The Offering page.'}
            </p>
            {!search && statusFilter === 'all' && languageFilter === 'all' && (
              <Button onClick={() => navigate('/about/offering?tab=itineraries')}>
                Go to The Offering
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Service Grid */}
      {!loading && !error && services.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service) => (
              <Card
                key={service.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/about/offering?tab=itineraries')}
              >
                <CardContent className="pt-6 space-y-3">
                  {/* Image placeholder */}
                  <div className="h-32 bg-muted rounded flex items-center justify-center">
                    {service.image_url ? (
                      <img
                        src={service.image_url}
                        alt={service.name}
                        className="h-full w-full object-cover rounded"
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-semibold text-sm line-clamp-2">{service.name}</h3>

                  {/* Duration + Price */}
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    {service.duration_minutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(service.duration_minutes)}
                      </span>
                    )}
                    {service.price_from != null && (
                      <span>
                        From {service.currency || '$'}{service.price_from.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Status badge */}
                  <div className="flex gap-2">
                    <Badge variant={statusVariant(service.status)}>
                      {service.status}
                    </Badge>
                    {service.language && (
                      <Badge variant="outline">{service.language.toUpperCase()}</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, totalCount)} of {totalCount.toLocaleString()} services
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
